import { useTheme } from '@emotion/react';
import styled, { CSSObject } from '@emotion/styled';
import { Box, SelectChangeEvent } from '@mui/material';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../store';
import { selectCompanyCustomization } from '../../store/entities/companyCustomization';
import { getCompanyPages, selectCompanyPages, selectCompanyPagesState } from '../../store/entities/companyPages';
import { FetchState } from '../../store/fetchState';
import { SUPPORTED_LANGUAGES } from '../../utils/i18n';
import { QcsFormControl } from '../common/basic/QcsFormControl';
import { QcsInputLabel } from '../common/basic/QcsInputLabel';
import { QcsMenuItem } from '../common/basic/QcsMenuItem';
import { QcsSelect } from '../common/basic/QcsSelect';
import { ErrorAlert } from '../common/ErrorAlert';
import { LanguageSelectWithoutFormik } from '../common/form/LanguageSelectWithoutFormik';
import { StyledLoadingButton } from '../common/form/SubmitButton';
import { HtmlEditor } from '../common/html/HtmlEditor';
import { Loader } from '../common/Loader';
import { ContentWrapperColumn } from '../layout/commonLayout';
import { QcsBox } from '../common/basic/QcsBox';
import { PageType } from '@api/models/enums/PageType';
import { companyService } from '@api/services/services';
import { resources } from '@api/resources';
import { ShowError } from '../ShowError';

const AlertWrapper = styled.div((): CSSObject => ({
  padding: '1rem',
}));

const SelectContainer = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
  [theme.breakpoints.up('lg')]: {
    flexDirection: 'row',
  },
}));

export const CompSettTabPages: FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const companyPagesState = useAppSelector(selectCompanyPagesState);
  const companyCustomization = useAppSelector(selectCompanyCustomization);
  const companyPages = useAppSelector(selectCompanyPages);

  const getHtmlRef = useRef<(() => string) | null>(null);
  const reloadHtmlRef = useRef<(() => void) | null>(null);

  const [pageType, setPageType] = useState<PageType>(PageType.SupplierManager);
  const [language, setLanguage] = useState<string | undefined>(companyCustomization?.applicationLanguages[0]);

  useEffect(() => {
    dispatch(getCompanyPages());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const page = useMemo(
    () => companyPages?.find((page) => page.type === pageType && page.language.toLowerCase() === language?.toLowerCase()),
    [companyPages, language, pageType],
  );

  useEffect(() => {
    reloadHtmlRef.current?.();
  }, [page]);

  // after-training pages are authored in the training languages, everything else in the application languages.
  // the backend can return codes in mixed case (e.g. "DE"), so normalize to the lowercase codes the app uses.
  const getTypeLanguages = (type: PageType) =>
    (type === PageType.AfterTraining ? companyCustomization?.testLanguages : companyCustomization?.applicationLanguages)?.map((code) =>
      code.toLowerCase(),
    );

  const allowedLanguages = getTypeLanguages(pageType);

  const handleTypeChange = (event: SelectChangeEvent<any>) => {
    const newType: PageType = event.target.value;
    setPageType(newType);

    const newAllowedLanguages = getTypeLanguages(newType);
    if (language && newAllowedLanguages?.length && !newAllowedLanguages.includes(language)) {
      setLanguage(newAllowedLanguages[0]);
    }
  };

  const handleLanguageChange = (event: SelectChangeEvent<any>) => {
    setLanguage(event.target.value);
  };

  const handleSubmit = async () => {
    const data = getHtmlRef.current?.();

    await companyService.updateCompanyPage({
      pages: [
        {
          type: pageType,
          language: language!,
          content: data!,
        },
      ],
    });
    dispatch(getCompanyPages());
  };

  if (companyPagesState === FetchState.Error) {
    return (
      <AlertWrapper>
        <ErrorAlert />
      </AlertWrapper>
    );
  }

  if (companyPages === undefined || !language) {
    return (
      <AlertWrapper>
        <Loader />
      </AlertWrapper>
    );
  }

  return (
    <Box mt={2}>
      <ContentWrapperColumn>
        <SelectContainer>
          <QcsFormControl>
            <QcsInputLabel>{t('settings.companyPages.type')}</QcsInputLabel>
            <QcsSelect
              sx={{
                backgroundColor: theme.palette.common.white,
                width: theme.breakpoints.values.sm / 2,
              }}
              label={t('settings.companyPages.type')}
              value={pageType}
              onChange={handleTypeChange}
            >
              {Object.values(PageType).map((x) => (
                <QcsMenuItem key={x} value={x}>
                  {t(`settings.companyPages.types.${x}`)}
                </QcsMenuItem>
              ))}
            </QcsSelect>
          </QcsFormControl>

          <LanguageSelectWithoutFormik
            sx={{
              width: theme.breakpoints.values.sm / 2,
            }}
            languages={SUPPORTED_LANGUAGES}
            allowedLanguages={allowedLanguages}
            value={language}
            label={t('settings.companyPages.language')}
            onChange={handleLanguageChange}
          />
        </SelectContainer>

        {!!page &&
          (companyPagesState !== FetchState.Loading ? (
            <>
              <HtmlEditor html={page.content} getHtmlRef={getHtmlRef} reloadHtmlRef={reloadHtmlRef} allowSelectRisks={false} />
              <ShowError resource={[resources.queryCompanyPages, resources.updateCompanyPage]} />
              <QcsBox sx={{ maxWidth: '980px', width: '100%' }}>
                <StyledLoadingButton
                  sx={{ display: 'block' }}
                  resourceLoading={[resources.queryCompanyPages, resources.updateCompanyPage]}
                  variant="contained"
                  onClick={handleSubmit}
                >
                  {t('common.save')}
                </StyledLoadingButton>
              </QcsBox>
            </>
          ) : (
            <AlertWrapper>
              <Loader />
            </AlertWrapper>
          ))}
      </ContentWrapperColumn>
    </Box>
  );
};
